import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FileCheck,
  Clock,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Upload,
  Eye,
  Search,
  Filter,
  Image as ImageIcon,
  User,
  Calendar,
  ChevronDown,
  Loader2
} from 'lucide-react';
import { Button } from '../common/Button';
import { Input } from '../common/Input';
import { SlipSubmission, SubmissionStatus } from '../../types';
import { useSlipSubmissions } from '../../hooks/useFirebaseData';
import { TestUploadModal } from './TestUploadModal';
import { SlipReviewModal } from './SlipReviewModal';
import { safeDistanceToNow as formatDistanceToNow } from '../../utils/safeFormat';

// Status configuration
const STATUS_CONFIG: Record<SubmissionStatus, { label: string; color: string; bgColor: string; icon: React.ReactNode }> = {
  pending: {
    label: 'Pending',
    color: 'text-warning',
    bgColor: 'bg-warning/10',
    icon: <Clock size={14} />
  },
  processing: {
    label: 'Processing',
    color: 'text-info',
    bgColor: 'bg-info/10',
    icon: <Loader2 size={14} className="animate-spin" />
  },
  approved: {
    label: 'Approved',
    color: 'text-success',
    bgColor: 'bg-success/10',
    icon: <CheckCircle size={14} />
  },
  rejected: {
    label: 'Rejected',
    color: 'text-error',
    bgColor: 'bg-error/10',
    icon: <XCircle size={14} />
  },
  flagged: {
    label: 'Flagged',
    color: 'text-orange-500',
    bgColor: 'bg-orange-500/10',
    icon: <AlertTriangle size={14} />
  }
};

// Submission Card Component
const SubmissionCard: React.FC<{
  submission: SlipSubmission;
  onReview: () => void;
}> = ({ submission, onReview }) => {
  const status = STATUS_CONFIG[submission.status];

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="card p-4 hover:shadow-lg transition-shadow cursor-pointer"
      onClick={onReview}
    >
      <div className="flex gap-4">
        {/* Image Preview */}
        <div className="w-24 h-24 bg-surface-light dark:bg-gray-800 rounded-lg overflow-hidden flex-shrink-0">
          {submission.imageURL ? (
            <img
              src={submission.imageURL}
              alt="Slip"
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <ImageIcon size={32} className="text-text-secondary-light dark:text-text-secondary-dark" />
            </div>
          )}
        </div>

        {/* Details */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="flex items-center gap-2">
              {submission.userPhotoURL ? (
                <img
                  src={submission.userPhotoURL}
                  alt={submission.username || 'User'}
                  className="w-6 h-6 rounded-full"
                />
              ) : (
                <div className="w-6 h-6 bg-primary/10 rounded-full flex items-center justify-center">
                  <User size={14} className="text-primary" />
                </div>
              )}
              <span className="font-medium text-sm">@{submission.username || 'Unknown'}</span>
            </div>
            <span className={`px-2 py-0.5 rounded-full text-xs font-medium flex items-center gap-1 ${status.bgColor} ${status.color}`}>
              {status.icon}
              {status.label}
            </span>
          </div>

          <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-text-secondary-light dark:text-text-secondary-dark">
            <span className="flex items-center gap-1">
              <Calendar size={12} />
              {formatDistanceToNow(submission.createdAt, { addSuffix: true })}
            </span>
            <span className="uppercase font-medium text-[10px] px-1.5 py-0.5 bg-surface-light dark:bg-gray-800 rounded">
              {submission.source}
            </span>
            {submission.priority > 0 && (
              <span className="text-warning font-medium">
                {submission.priority === 2 ? 'URGENT' : 'HIGH PRIORITY'}
              </span>
            )}
          </div>

          {/* Processing Error */}
          {(submission.processingError || submission.processingResult?.error) && (
            <div className="mt-2 p-2 bg-error/10 border border-error/20 rounded-lg">
              <div className="flex items-center gap-1.5 text-xs text-error font-medium">
                <XCircle size={12} />
                {submission.processingResult?.userMessage?.title || 'Processing Error'}
              </div>
              <p className="mt-1 text-xs text-error/80">
                {submission.processingResult?.userMessage?.message || submission.processingError}
              </p>
            </div>
          )}

          {/* AI Extraction Preview */}
          {submission.aiExtraction && (
            <div className="mt-2 space-y-1">
              <div className="flex flex-wrap gap-1 text-xs">
                {submission.aiExtraction.sportsbook && (
                  <span className="inline-block px-1.5 py-0.5 bg-primary/10 text-primary rounded">
                    {submission.aiExtraction.sportsbook}
                  </span>
                )}
                {submission.aiExtraction.betType && (
                  <span className="inline-block px-1.5 py-0.5 bg-info/10 text-info rounded">
                    {submission.aiExtraction.betType}
                  </span>
                )}
                {submission.aiExtraction.confidence !== undefined && (
                  <span className={`inline-block px-1.5 py-0.5 rounded ${
                    submission.aiExtraction.confidence >= 70
                      ? 'bg-success/10 text-success'
                      : submission.aiExtraction.confidence >= 40
                      ? 'bg-warning/10 text-warning'
                      : 'bg-error/10 text-error'
                  }`}>
                    Confidence: {submission.aiExtraction.confidence}%
                  </span>
                )}
                {submission.qualityAnalysis?.score !== undefined && (
                  <span className={`inline-block px-1.5 py-0.5 rounded ${
                    submission.qualityAnalysis.score >= 70
                      ? 'bg-success/10 text-success'
                      : submission.qualityAnalysis.score >= 50
                      ? 'bg-warning/10 text-warning'
                      : 'bg-error/10 text-error'
                  }`}>
                    Quality: {submission.qualityAnalysis.score}%
                  </span>
                )}
                {submission.aiExtraction.picks?.length > 0 && (
                  <span className="inline-block px-1.5 py-0.5 bg-gray-100 dark:bg-gray-800 rounded">
                    {submission.aiExtraction.picks.length} pick{submission.aiExtraction.picks.length !== 1 ? 's' : ''}
                  </span>
                )}
              </div>
              {/* AI User Message */}
              {submission.processingResult?.userMessage && !submission.processingResult?.error && (
                <div className={`text-xs px-2 py-1 rounded ${
                  submission.processingResult.userMessage.color === 'success' ? 'bg-success/10 text-success' :
                  submission.processingResult.userMessage.color === 'warning' ? 'bg-warning/10 text-warning' :
                  submission.processingResult.userMessage.color === 'error' ? 'bg-error/10 text-error' :
                  'bg-info/10 text-info'
                }`}>
                  {submission.processingResult.userMessage.title}: {submission.processingResult.userMessage.message}
                </div>
              )}
              {/* Flags */}
              {submission.aiExtraction.flags && submission.aiExtraction.flags.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {submission.aiExtraction.flags.map((flag: string, idx: number) => (
                    <span key={idx} className="text-[10px] px-1 py-0.5 bg-orange-500/10 text-orange-500 rounded">
                      {flag.replace(/_/g, ' ')}
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Duplicate Warning */}
          {submission.duplicateCheck?.isDuplicate && (
            <div className="mt-2 flex items-center gap-1 text-xs text-orange-500">
              <AlertTriangle size={12} />
              Potential duplicate detected
            </div>
          )}
        </div>

        {/* Action Button */}
        <div className="flex-shrink-0">
          <Button
            variant="secondary"
            size="sm"
            icon={<Eye size={16} />}
            onClick={(e) => {
              e.stopPropagation();
              onReview();
            }}
          >
            Review
          </Button>
        </div>
      </div>
    </motion.div>
  );
};

// Main Component
export const SlipVerificationTab: React.FC = () => {
  const [statusFilter, setStatusFilter] = useState<SubmissionStatus | 'all'>('pending');
  const [searchQuery, setSearchQuery] = useState('');
  const [showTestUpload, setShowTestUpload] = useState(false);
  const [reviewingSubmission, setReviewingSubmission] = useState<SlipSubmission | null>(null);
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);

  // Fetch submissions based on filter
  const { submissions: pendingSubmissions, loading: pendingLoading } = useSlipSubmissions('pending');
  const { submissions: flaggedSubmissions, loading: flaggedLoading } = useSlipSubmissions('flagged');
  const { submissions: allSubmissions, loading: allLoading } = useSlipSubmissions();

  // Combine submissions based on filter
  const filteredSubmissions = useMemo(() => {
    let subs: SlipSubmission[] = [];

    if (statusFilter === 'all') {
      subs = allSubmissions;
    } else if (statusFilter === 'pending') {
      // Show both pending and flagged in "pending" view
      subs = [...pendingSubmissions, ...flaggedSubmissions];
    } else {
      subs = allSubmissions.filter(s => s.status === statusFilter);
    }

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      subs = subs.filter(s =>
        (s.username?.toLowerCase() || '').includes(query) ||
        s.submissionId.toLowerCase().includes(query) ||
        s.aiExtraction?.sportsbook?.toLowerCase().includes(query)
      );
    }

    // Sort by priority (urgent first) then by date
    return subs.sort((a, b) => {
      if (a.priority !== b.priority) return b.priority - a.priority;
      return b.createdAt - a.createdAt;
    });
  }, [statusFilter, searchQuery, pendingSubmissions, flaggedSubmissions, allSubmissions]);

  const isLoading = pendingLoading || flaggedLoading || allLoading;

  // Stats
  const stats = useMemo(() => ({
    pending: pendingSubmissions.length,
    flagged: flaggedSubmissions.length,
    approved: allSubmissions.filter(s => s.status === 'approved').length,
    rejected: allSubmissions.filter(s => s.status === 'rejected').length,
    total: allSubmissions.length
  }), [pendingSubmissions, flaggedSubmissions, allSubmissions]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      className="space-y-6"
    >
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold flex items-center gap-2">
            <FileCheck size={24} className="text-primary" />
            Slip Verification
          </h2>
          <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark mt-1">
            Review and verify user-submitted betting slips
          </p>
        </div>
        <Button
          variant="primary"
          icon={<Upload size={18} />}
          onClick={() => setShowTestUpload(true)}
        >
          Test Upload
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="card p-4">
          <div className="flex items-center gap-2 mb-1">
            <Clock size={16} className="text-warning" />
            <span className="text-xs text-text-secondary-light dark:text-text-secondary-dark">Pending</span>
          </div>
          <p className="text-2xl font-bold">{stats.pending + stats.flagged}</p>
        </div>
        <div className="card p-4">
          <div className="flex items-center gap-2 mb-1">
            <CheckCircle size={16} className="text-success" />
            <span className="text-xs text-text-secondary-light dark:text-text-secondary-dark">Approved</span>
          </div>
          <p className="text-2xl font-bold text-success">{stats.approved}</p>
        </div>
        <div className="card p-4">
          <div className="flex items-center gap-2 mb-1">
            <XCircle size={16} className="text-error" />
            <span className="text-xs text-text-secondary-light dark:text-text-secondary-dark">Rejected</span>
          </div>
          <p className="text-2xl font-bold text-error">{stats.rejected}</p>
        </div>
        <div className="card p-4">
          <div className="flex items-center gap-2 mb-1">
            <AlertTriangle size={16} className="text-orange-500" />
            <span className="text-xs text-text-secondary-light dark:text-text-secondary-dark">Flagged</span>
          </div>
          <p className="text-2xl font-bold text-orange-500">{stats.flagged}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1">
          <Input
            placeholder="Search by username or submission ID..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            icon={<Search size={18} />}
          />
        </div>
        <div className="relative">
          <Button
            variant="secondary"
            icon={<Filter size={18} />}
            onClick={() => setShowFilterDropdown(!showFilterDropdown)}
          >
            {statusFilter === 'all' ? 'All Status' : STATUS_CONFIG[statusFilter]?.label || statusFilter}
            <ChevronDown size={16} className="ml-1" />
          </Button>
          <AnimatePresence>
            {showFilterDropdown && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="absolute right-0 mt-2 w-48 bg-white dark:bg-surface-dark rounded-lg shadow-xl border border-border-light dark:border-border-dark z-50"
              >
                <button
                  className={`w-full px-4 py-2 text-left text-sm hover:bg-surface-light dark:hover:bg-gray-800 first:rounded-t-lg ${
                    statusFilter === 'all' ? 'bg-primary/10 text-primary' : ''
                  }`}
                  onClick={() => { setStatusFilter('all'); setShowFilterDropdown(false); }}
                >
                  All Status
                </button>
                {(Object.keys(STATUS_CONFIG) as SubmissionStatus[]).map((status) => (
                  <button
                    key={status}
                    className={`w-full px-4 py-2 text-left text-sm hover:bg-surface-light dark:hover:bg-gray-800 flex items-center gap-2 ${
                      statusFilter === status ? 'bg-primary/10 text-primary' : ''
                    } last:rounded-b-lg`}
                    onClick={() => { setStatusFilter(status); setShowFilterDropdown(false); }}
                  >
                    {STATUS_CONFIG[status].icon}
                    {STATUS_CONFIG[status].label}
                  </button>
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Submissions List */}
      <div className="space-y-4">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 size={32} className="animate-spin text-primary" />
          </div>
        ) : filteredSubmissions.length === 0 ? (
          <div className="card p-12 text-center">
            <FileCheck size={48} className="mx-auto text-text-secondary-light dark:text-text-secondary-dark mb-4" />
            <h3 className="text-lg font-bold mb-2">No Submissions</h3>
            <p className="text-text-secondary-light dark:text-text-secondary-dark mb-4">
              {statusFilter === 'pending'
                ? "All caught up! No pending submissions to review."
                : `No ${statusFilter === 'all' ? '' : statusFilter} submissions found.`}
            </p>
            <Button
              variant="secondary"
              icon={<Upload size={18} />}
              onClick={() => setShowTestUpload(true)}
            >
              Create Test Submission
            </Button>
          </div>
        ) : (
          filteredSubmissions.map((submission) => (
            <SubmissionCard
              key={submission.submissionId}
              submission={submission}
              onReview={() => setReviewingSubmission(submission)}
            />
          ))
        )}
      </div>

      {/* Modals */}
      <AnimatePresence>
        {showTestUpload && (
          <TestUploadModal
            onClose={() => setShowTestUpload(false)}
          />
        )}
        {reviewingSubmission && (
          <SlipReviewModal
            submission={reviewingSubmission}
            onClose={() => setReviewingSubmission(null)}
          />
        )}
      </AnimatePresence>
    </motion.div>
  );
};
